import { getCSRFToken } from '../lib/django'

const API_BASE = (process.env.REACT_APP_API_BASE || 'http://localhost:8000') + '/api/tti'

export async function renderReportPdf (document) {
  const body = new FormData()
  body.append('document', new Blob([JSON.stringify(document)], { type: 'application/json' }), 'report.json')
  const response = await fetch(`${API_BASE}/reports/render-pdf/`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/pdf', 'X-CSRFToken': getCSRFToken() },
    body
  })
  if (!response.ok) {
    const message = `Unable to render report PDF (${response.status}).`
    if (!response.headers.get('Content-Type')?.includes('application/json')) throw new Error(message)
    const payload = await response.json()
    throw new Error(payload.error || message)
  }
  if (!response.headers.get('Content-Type')?.includes('application/pdf')) throw new Error('The server did not return a valid report PDF.')
  return response.blob()
}
const referenceCache = new Map()
let cacheGeneration = 0
export const ITEM_LIBRARY_CHANGED_EVENT = 'item-library-changed'
export const COLLECTION_TEMPLATE_CHANGED_EVENT = 'collection-template-changed'
export const DEFAULT_COLLECTION_TEMPLATE_URL = '/templates/pcf-data-template.xlsx'

function collectionTemplatePayload (payload) {
  return { ...payload, download_url: payload.is_default ? DEFAULT_COLLECTION_TEMPLATE_URL : `${API_BASE}/item-library/collection-template/download/` }
}

export const getCollectionTemplate = () => carbonRequest('GET', '/item-library/collection-template/', undefined, { refresh: true }).then(collectionTemplatePayload)
export const replaceCollectionTemplate = (file) => {
  const form = new FormData()
  form.append('file', file)
  return carbonFormRequest('POST', '/item-library/collection-template/', form, { invalidateCaches: false }).then((payload) => {
    const template = collectionTemplatePayload(payload)
    window.dispatchEvent(new CustomEvent(COLLECTION_TEMPLATE_CHANGED_EVENT, { detail: template }))
    return template
  })
}
export const PROJECT_MOVED_EVENT = 'project-moved'

export function invalidateProjectData () {
  clearReferenceCache()
  clearListCache()
}

export function invalidateItemLibraryReferences () {
  invalidateProjectData()
}

function notifyItemLibraryChanged (payload) {
  invalidateItemLibraryReferences()
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(ITEM_LIBRARY_CHANGED_EVENT, { detail: payload }))
  return payload
}
const pendingGetRequests = new Map()
const listCache = new Map()
const LIST_CACHE_TTL_MS = 5 * 60 * 1000
const STALE_LIST_CACHE_TTL_MS = 30 * 60 * 1000

let accountGeneration = 0

export function invalidateAccountData () {
  accountGeneration += 1
  clearListCache()
  clearReferenceCache()
}

if (typeof document !== 'undefined') {
  document.addEventListener('allauth.auth.change', invalidateAccountData)
  window.addEventListener('project-access-changed', invalidateAccountData)
}

function nowMs () {
  return Date.now()
}

function readListCache (path, ttlMs = LIST_CACHE_TTL_MS) {
  const memoryCache = listCache.get(path)
  if (memoryCache && nowMs() - memoryCache.timestamp < ttlMs) return memoryCache.payload
  return null
}

function writeListCache (path, payload) {
  const cachedPayload = { ...payload, _cachedAt: nowMs() }
  listCache.set(path, { timestamp: nowMs(), payload: cachedPayload })
  return cachedPayload
}

function clearListCache () {
  listCache.clear()
  if (typeof window === 'undefined' || !window.sessionStorage) return
  try {
    Object.keys(window.sessionStorage)
      .filter((key) => key.startsWith('carbon:list:'))
      .forEach((key) => window.sessionStorage.removeItem(key))
  } catch (error) {}
}

function clearReferenceCache () {
  cacheGeneration += 1
  referenceCache.clear()
  pendingGetRequests.clear()
}

async function cachedCarbonGet (path, ttlMs = 300000) {
  const now = Date.now()
  const cached = referenceCache.get(path)
  if (cached && now - cached.timestamp < ttlMs) return cached.payload
  const generation = cacheGeneration
  const payload = await carbonRequest('GET', path)
  if (generation === cacheGeneration) referenceCache.set(path, { timestamp: now, payload })
  return payload
}

async function carbonRequest (method, path, data, { invalidateCaches = method !== 'GET', refresh = false } = {}) {
  if (invalidateCaches) {
    clearListCache()
    clearReferenceCache()
  }
  const requestKey = method === 'GET' && data === undefined && !refresh ? path : ''
  if (requestKey && pendingGetRequests.has(requestKey)) return pendingGetRequests.get(requestKey)
  const options = {
    method,
    headers: {
      Accept: 'application/json',
      'X-CSRFToken': getCSRFToken()
    },
    credentials: 'include'
  }
  if (refresh) options.cache = 'no-store'
  if (data !== undefined) {
    options.headers['Content-Type'] = 'application/json'
    options.body = JSON.stringify(data)
  }
  const account = accountGeneration
  const request = fetch(`${API_BASE}${path}`, options)
    .then(async (response) => {
      if (account !== accountGeneration) throw new Error('Account changed. Reloading authorized data.')
      if (!response.ok) {
        const responseText = await response.text()
        if (account !== accountGeneration) throw new Error('Account changed. Reloading authorized data.')
        let body = { error: responseText }
        try {
          body = responseText ? JSON.parse(responseText) : body
        } catch (error) {}
        const error = new Error(body.error || body.detail || `API error ${response.status}`)
        error.status = response.status
        error.body = body
        throw error
      }
      if (response.status === 204) return null
      const payload = await response.json()
      if (account !== accountGeneration) throw new Error('Account changed. Reloading authorized data.')
      return payload
    })
    .finally(() => {
      if (requestKey && pendingGetRequests.get(requestKey) === request) pendingGetRequests.delete(requestKey)
    })
  if (requestKey) pendingGetRequests.set(requestKey, request)
  return request
}

function cachedListRequest (path, { ttlMs = LIST_CACHE_TTL_MS, staleTtlMs = STALE_LIST_CACHE_TTL_MS } = {}) {
  const cached = readListCache(path, ttlMs)
  if (cached) return Promise.resolve(cached)
  const stale = readListCache(path, staleTtlMs)
  const generation = cacheGeneration
  const refresh = carbonRequest('GET', path).then((payload) => generation === cacheGeneration ? writeListCache(path, payload) : payload)
  if (stale) {
    return Promise.resolve({ ...stale, _refresh: refresh.catch(() => stale) })
  }
  return refresh
}

async function carbonFormRequest (method, path, formData, { invalidateCaches = method !== 'GET' } = {}) {
  if (invalidateCaches) {
    clearListCache()
    clearReferenceCache()
  }
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      'X-CSRFToken': getCSRFToken()
    },
    credentials: 'include',
    body: formData
  })
  if (!response.ok) {
    const responseText = await response.text()
    let body = { error: responseText }
    try {
      body = responseText ? JSON.parse(responseText) : body
    } catch (error) {}
    throw new Error(body.error || body.detail || `API error ${response.status}`)
  }
  if (response.status === 204) return null
  return response.json()
}

async function carbonDownloadRequest (method, path, data) {
  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      Accept: 'application/octet-stream',
      'Content-Type': 'application/json',
      'X-CSRFToken': getCSRFToken()
    },
    credentials: 'include',
    ...(method === 'GET' ? {} : { body: JSON.stringify(data || {}) })
  })
  if (!response.ok) {
    const responseText = await response.text()
    let body = { error: responseText }
    try {
      body = responseText ? JSON.parse(responseText) : body
    } catch (error) {}
    throw new Error(body.error || body.detail || `API error ${response.status}`)
  }
  const disposition = response.headers.get('Content-Disposition') || ''
  const filenameMatch = disposition.match(/filename="?([^";]+)"?/i)
  const encodedFilenameMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  let filename = filenameMatch?.[1] || 'stk-batch-export'
  if (encodedFilenameMatch) {
    try { filename = decodeURIComponent(encodedFilenameMatch[1]) } catch (error) {}
  }
  return { blob: await response.blob(), filename }
}

export const checkProductNumber = (number, projectId) => carbonRequest('GET', `/product-number/?number=${encodeURIComponent(number || '')}${projectId ? `&exclude_project_id=${encodeURIComponent(projectId)}` : ''}`).then(payload => ({ ...payload, exists: false })).catch(error => {
  if (error.body?.code === 'product_number_exists') return { ...error.body, exists: true }
  throw error
})
export const listAccessRequests = () => carbonRequest('GET', '/access-requests/')
export const requestProjectAccess = (projectId) => carbonRequest('POST', `/projects/${projectId}/access-requests/`, {})
export const decideProjectAccess = (projectId, requestId, role) => carbonRequest('PATCH', `/projects/${projectId}/access-requests/`, { request_id: requestId, role }).then(payload => {
  window.dispatchEvent(new CustomEvent('project-access-changed'))
  return payload
})
export const revokeProjectAccess = (projectId, requestId) => carbonRequest('DELETE', `/projects/${projectId}/access-requests/`, { request_id: requestId }).then(payload => {
  window.dispatchEvent(new CustomEvent('project-access-changed'))
  return payload
})

export const listProjects = ({ search = '', page = 1, pageSize = 20, sort = 'changed', direction = 'desc', status = 'all', changed = 'all' } = {}) => {
  const params = new URLSearchParams()
  if (search) params.set('search', search)
  if (page) params.set('page', page)
  if (pageSize) params.set('page_size', pageSize)
  if (sort) params.set('sort', sort)
  if (direction) params.set('direction', direction)
  if (status && status !== 'all') params.set('status', status)
  if (changed && changed !== 'all') params.set('changed', changed)
  const query = params.toString()
  return cachedListRequest(`/projects/${query ? `?${query}` : ''}`)
}
export const listGroupedProjects = ({ search = '', type = 'all', page = 1, pageSize = 3, sort = 'changed', direction = 'desc', status = 'all', changed = 'all' } = {}) => {
  const params = new URLSearchParams()
  if (search) params.set('search', search)
  if (type) params.set('type', type)
  if (page) params.set('page', page)
  if (pageSize) params.set('page_size', pageSize)
  if (sort) params.set('sort', sort)
  if (direction) params.set('direction', direction)
  if (status && status !== 'all') params.set('status', status)
  if (changed && changed !== 'all') params.set('changed', changed)
  const query = params.toString()
  return cachedListRequest(`/projects/grouped/${query ? `?${query}` : ''}`)
}
export const listUmbrellaProjects = ({ search = '', page = 1, pageSize = 20, owned = false } = {}) => {
  const params = new URLSearchParams()
  if (owned) params.set('owned', 'true')
  if (search) params.set('search', search)
  if (page) params.set('page', page)
  if (pageSize) params.set('page_size', pageSize)
  const query = params.toString()
  return carbonRequest('GET', `/umbrella-projects/${query ? `?${query}` : ''}`)
}
export const createUmbrellaProject = (payload) => carbonRequest('POST', '/umbrella-projects/', payload)
export const getUmbrellaProject = (projectId) => carbonRequest('GET', `/umbrella-projects/${projectId}/`)
export const listProjectContributors = (projectId) => carbonRequest('GET', `/umbrella-projects/${projectId}/access/`)
export const shareUmbrellaProject = (projectId, email, role = 'view') => carbonRequest('POST', `/umbrella-projects/${projectId}/access/`, { email, role }).then(payload => {
  clearListCache()
  return payload
})
export const removeProjectContributor = (projectId, grantId) => carbonRequest('DELETE', `/umbrella-projects/${projectId}/access/${grantId}/`).then(payload => {
  clearListCache()
  return payload
})
export const updateUmbrellaProject = (projectId, payload) => carbonRequest('PATCH', `/umbrella-projects/${projectId}/`, payload)
export const deleteUmbrellaProject = (projectId) => carbonRequest('DELETE', `/umbrella-projects/${projectId}/`)
export const getBatchProject = (batchIndex) => carbonRequest('GET', `/batch-projects/${encodeURIComponent(batchIndex)}/`)
export const updateBatchProject = (batchIndex, payload) => carbonRequest('PATCH', `/batch-projects/${encodeURIComponent(batchIndex)}/`, payload)
export const deleteBatchProject = (batchIndex) => carbonRequest('DELETE', `/batch-projects/${encodeURIComponent(batchIndex)}/`)
export const listUmbrellaBatches = (projectId) => carbonRequest('GET', `/umbrella-projects/${projectId}/batches/`)
export const createUmbrellaBatch = (projectId, payload) => carbonRequest('POST', `/umbrella-projects/${projectId}/batches/`, payload)
export const getBatch = (batchId) => carbonRequest('GET', `/batches/${batchId}/`)
export const updateBatch = (batchId, payload) => carbonRequest('PATCH', `/batches/${batchId}/`, payload)
export const deleteBatch = (batchId) => carbonRequest('DELETE', `/batches/${batchId}/`)
export const listBatchProducts = (batchId) => carbonRequest('GET', `/batches/${batchId}/products/`)
export const createBatchProduct = (batchId, payload) => carbonRequest('POST', `/batches/${batchId}/products/`, payload)
export const duplicateProject = (projectId) => carbonRequest('POST', `/projects/${encodeURIComponent(projectId)}/duplicate/`, {})
export const getProjectMoveOptions = (projectId) => carbonRequest('GET', `/projects/${encodeURIComponent(projectId)}/move/`)
export const moveProject = (projectId, { umbrella_project_id, source_umbrella_project_id, base_updated_at }) => carbonRequest('POST', `/projects/${encodeURIComponent(projectId)}/move/`, { umbrella_project_id, source_umbrella_project_id, base_updated_at }).then(payload => {
  invalidateProjectData()
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(PROJECT_MOVED_EVENT, { detail: payload }))
  return payload
})
export const getProduct = (productId) => carbonRequest('GET', `/products/${productId}/`)
export const updateProduct = (productId, payload) => carbonRequest('PATCH', `/products/${productId}/`, payload)
export const deleteProduct = (productId) => carbonRequest('DELETE', `/products/${productId}/`)
export const listProductReports = (productId) => carbonRequest('GET', `/products/${productId}/reports/`)
export const createProductReport = (productId, payload) => carbonRequest('POST', `/products/${productId}/reports/`, payload)
export const getReport = (reportId) => carbonRequest('GET', `/reports/${reportId}/`)
export const updateReport = (reportId, payload) => carbonRequest('PATCH', `/reports/${reportId}/`, payload)
export const deleteReport = (reportId) => carbonRequest('DELETE', `/reports/${reportId}/`)
export const listReportVersions = (reportId, options = {}) => {
  const params = new URLSearchParams()
  if (options.scope) params.set('scope', options.scope)
  const search = params.toString()
  return carbonRequest('GET', `/reports/${reportId}/versions/${search ? `?${search}` : ''}`)
}
export const createReportVersionDraft = (reportId, payload = {}) => carbonRequest('POST', `/reports/${reportId}/versions/`, payload)
export const getReportVersion = (reportId, versionId, { editing = false } = {}) => carbonRequest('GET', `/reports/${reportId}/versions/${versionId}/${editing ? '?editing=1' : ''}`)
export const updateReportVersion = (reportId, versionId, payload = {}) => carbonRequest('PATCH', `/reports/${reportId}/versions/${versionId}/`, payload)
export const finalizeReportVersion = (reportId, versionId, payload = {}) => carbonRequest('POST', `/reports/${reportId}/versions/${versionId}/finalize/`, payload)
export const deleteReportVersion = (reportId, versionId) => carbonRequest('DELETE', `/reports/${reportId}/versions/${versionId}/delete/`)
export const discardReportVersionDraft = (reportId, versionId) => carbonRequest('DELETE', `/reports/${reportId}/versions/${versionId}/discard/`)
export const createProject = (payload) => carbonRequest('POST', '/projects/', payload)
export const getProject = (projectId) => carbonRequest('GET', `/projects/${projectId}/`)
export const updateProject = (projectId, payload) => carbonRequest('PATCH', `/projects/${projectId}/`, payload)
export const updateProjectBomFast = (projectId, products) => carbonRequest('PATCH', `/projects/${projectId}/`, { products, response_mode: 'bom', version_mode: 'defer' })
export const deleteProject = (projectId) => carbonRequest('DELETE', `/projects/${projectId}/`)
export const addProjectProduct = (projectId, payload) => carbonRequest('POST', `/projects/${projectId}/products/`, payload)
export const addProjectAssembly = (projectId, payload) => carbonRequest('POST', `/projects/${projectId}/assemblies/`, payload)
export const addProjectPart = (projectId, payload) => carbonRequest('POST', `/projects/${projectId}/parts/`, payload)
export const searchBomParts = (query = '', level = 'part') => {
  const params = new URLSearchParams()
  if (query) params.set('q', query)
  if (level) params.set('level', level)
  const search = params.toString()
  return carbonRequest('GET', `/bom/parts/search/${search ? `?${search}` : ''}`)
}
export const saveProjectSourcing = (projectId, payload) => carbonRequest('POST', `/projects/${projectId}/sourcing/`, payload)
export const saveProjectDistribution = (projectId, payload) => carbonRequest('POST', `/projects/${projectId}/distribution/`, payload)
export const previewProjectBomImport = (files, { sheet = '', projectName = '', partNumber = '' } = {}) => {
  const formData = new FormData()
  ;(Array.isArray(files) ? files : [files]).filter(Boolean).forEach((file) => formData.append('file', file))
  if (sheet) formData.append('sheet', sheet)
  if (projectName) formData.append('project_name', projectName)
  if (partNumber) formData.append('part_number', partNumber)
  return carbonFormRequest('POST', '/projects/bom/import-preview/', formData, { invalidateCaches: false })
}
export const downloadProjectBomSource = (projectId, sourceId) => carbonDownloadRequest('GET', `/projects/${encodeURIComponent(projectId)}/bom/sources/${encodeURIComponent(sourceId)}/download/`)
export const deleteProjectBomSource = (projectId, sourceId) => carbonRequest('DELETE', `/projects/${encodeURIComponent(projectId)}/bom/sources/${encodeURIComponent(sourceId)}/`)

export const importProjectBom = (projectId, files, sheet = '', versionId = null) => {
  const formData = new FormData()
  ;(Array.isArray(files) ? files : [files]).filter(Boolean).forEach((file) => formData.append('file', file))
  if (sheet) formData.append('sheet', sheet)
  if (versionId !== null) formData.append('version_id', String(versionId))
  return carbonFormRequest('POST', `/projects/${projectId}/bom/import/`, formData)
}
export const saveProjectWorkflowData = (projectId, payload) => carbonRequest('PATCH', `/projects/${projectId}/workflow-data/`, payload)
export const recalculateProjectItemLibrary = (projectId, versionId = null) => carbonRequest('POST', `/projects/${projectId}/item-library/recalculate/`, versionId ? { version_id: versionId } : {})
const expandedCatalogPayloads = new WeakMap()
function expandCatalogPayload (response) {
  if (response?.compact_schema !== 'catalog-aliases-v1') return response
  if (expandedCatalogPayloads.has(response)) return expandedCatalogPayloads.get(response)
  const payload = JSON.parse(JSON.stringify(response.payload))
  const { aliases } = response
  for (const [target, source] of aliases) {
    const value = source.reduce((current, key) => current[key], payload)
    const parent = target.slice(0, -1).reduce((current, key) => current[key], payload)
    // Keep live, published and workflow records independently editable after expansion.
    parent[target[target.length - 1]] = JSON.parse(JSON.stringify(value))
  }
  expandedCatalogPayloads.set(response, payload)
  return payload
}

export const getItemLibrary = () => carbonRequest('GET', '/item-library/?compact=1').then(expandCatalogPayload)
export const getItemLibraryUsage = (section, rowId) => carbonRequest('GET', `/item-library/usage/?section=${encodeURIComponent(section)}&row_id=${encodeURIComponent(rowId)}`)
export const getItemLibraryVersions = ({ management = false, refresh = false } = {}) => {
  const path = `/item-library/versions/${management ? '?management=true' : ''}`
  return refresh ? carbonRequest('GET', path, undefined, { refresh: true }) : cachedCarbonGet(path)
}
export const updateItemLibraryVersion = (payload) => carbonRequest('PATCH', '/item-library/versions/', payload).then(notifyItemLibraryChanged)
export const previewItemLibraryImport = (sections) => carbonRequest('POST', '/item-library/versions/', { sections, import_preview: true }, { invalidateCaches: false })
export const createItemLibraryVersion = (payload) => carbonRequest('POST', '/item-library/versions/', payload).then(notifyItemLibraryChanged)
export const getWorkflowOptions = () => cachedCarbonGet('/reference/workflow-options/')
export const getProjectMetadataOptions = () => cachedCarbonGet('/reference/project-metadata/')
export const getBatchIndexOptions = () => cachedCarbonGet('/reference/batch-indexes/')
export const getReferenceMaterials = () => carbonRequest('GET', '/reference/materials/')
export const getProjectReferenceMaterials = (projectId) => carbonRequest('GET', `/projects/${projectId}/reference/materials/`)
export const getReferencePartTypes = () => carbonRequest('GET', '/reference/part-types/')
export const getProjectReferencePartTypes = (projectId) => carbonRequest('GET', `/projects/${projectId}/reference/part-types/`)
export const getReferenceProcessTypesA = () => carbonRequest('GET', '/reference/process-types-a/')
export const getProjectReferenceProcessTypesA = (projectId) => carbonRequest('GET', `/projects/${projectId}/reference/process-types-a/`)
export const getReferenceProcessTypesB = () => carbonRequest('GET', '/reference/process-types-b/')
export const getProjectReferenceProcessTypesB = (projectId) => carbonRequest('GET', `/projects/${projectId}/reference/process-types-b/`)
export const getProjectBomReferences = (projectId, { fresh = false, versionId = null, itemLibraryVersionId = null } = {}) => {
  const path = `/projects/${projectId}/reference/bom-options/?compact=1${versionId ? `&version_id=${encodeURIComponent(versionId)}` : ''}${itemLibraryVersionId ? `&item_library_version_id=${encodeURIComponent(itemLibraryVersionId)}` : ''}`
  return (fresh ? carbonRequest('GET', path) : cachedCarbonGet(path)).then(expandCatalogPayload)
}
export const calculateCarbon = (payload) => carbonRequest('POST', '/carbon/calculate/', payload)
export const getCarbonReport = (projectId) => carbonRequest('GET', `/projects/${projectId}/report/`)
export const getBatchExportOptions = () => carbonRequest('GET', '/batch-export/options/')
export const getBatchExportReports = (payload) => carbonRequest('POST', '/batch-export/reports/', payload, { invalidateCaches: false })
export const downloadBatchExport = (payload) => carbonDownloadRequest('POST', '/batch-export/download/', payload)
